import { imagePaths } from '@/components/devicecontrol/VelocityConfiguration/Roles/helper';
import { ObjectLibraryFolder } from '@/model/DeviceAdminModel';
import { DirectoryTreeOnExpandParams, GraphicTreeNode, ObjectLibraryTreeNode } from '@/model/GraphicModel';
import { SearchType } from '@/model/RolesModel';
import { AppliedFilterIcon, ClosedFolderIcon, OpenedFolderIcon } from '@/resources/Icons';
import { Checkbox, Tree, Typography } from 'antd';
import { CheckboxChangeEvent } from 'antd/es/checkbox';
import React, { CSSProperties, useContext, useMemo } from 'react';
import { RoleStore } from '../../../RoleContext/context';
import styles from './hierarchyView.module.scss';

const { Paragraph } = Typography;

type Props = {
	data: ObjectLibraryTreeNode;
	searchType: SearchType;
	expandedKeys: React.Key[];
	autoExpandParent: boolean;
	isAnyFilterApplied: boolean;
	rebuildTreeByFilters: (searchType: SearchType, nodes: ObjectLibraryTreeNode[], parent: ObjectLibraryTreeNode) => ObjectLibraryTreeNode[];
	handleOnExpand: (expandedKeys: React.Key[], params: DirectoryTreeOnExpandParams) => void;
	handleOnChangeDeviceCheckbox: (event: CheckboxChangeEvent, node: ObjectLibraryTreeNode) => void;
};

type DeviceRenderNode = {
	searchType: SearchType;
	treeData: ObjectLibraryTreeNode[];
	expandedKeys: React.Key[];
	onChangeDeviceCheckbox: (event: CheckboxChangeEvent, node: ObjectLibraryTreeNode) => void;
};

const highlightStyle: React.CSSProperties = { color: '#1890ff', fontWeight: 'bold' };
const folderStyles: CSSProperties = { marginRight: '5px', fontSize: '24px' };

const HierarchyView: React.FC<Props> = ({
	data,
	searchType,
	expandedKeys,
	autoExpandParent,
	isAnyFilterApplied,
	rebuildTreeByFilters,
	handleOnExpand,
	handleOnChangeDeviceCheckbox,
}) => {
	//#region state management variables
	const {
		roleContextInitialState: {
			props: { roleId },
		},
	} = useContext(RoleStore);
	//#endregion

	const mapDeviceRenderNode = ({ treeData, searchType, expandedKeys, onChangeDeviceCheckbox }: DeviceRenderNode): GraphicTreeNode[] => {
		if (!treeData) {
			return treeData;
		}

		const searchText = searchType?.searchText?.trim()?.toLowerCase() ?? '';
		return treeData.map((item, dataIndex) => {
			const searchItems = searchType?.selectedSearchByHardwareTypes;
			const shouldSearch = searchItems === undefined || (searchItems !== undefined && searchItems.some(i => i === item.extendedData.objectType));

			const id = `name-${item.key}-${dataIndex}`;
			let index: number = -1;
			if (shouldSearch && item.folderType === ObjectLibraryFolder.ITEM) {
				index = item.displayName ? item.displayName.toLowerCase().indexOf(searchText) : -1;
			}

			let beforeStr: string = item.displayName;
			let matchStr: string = '';
			let afterStr: string = '';
			if (index >= 0) {
				beforeStr = item.displayName.substr(0, index);
				matchStr = item.displayName.substr(index, searchText.length);
				afterStr = item.displayName.substr(index + searchText.length);
			}

			const highlightedName: JSX.Element = (
				<span id={id}>
					{beforeStr}
					<span style={highlightStyle}>{matchStr}</span>
					{afterStr}
				</span>
			);

			const isItemExpanded = expandedKeys.includes(item.key);
			let isIndeterminate = undefined;
			if (item.isChecked) {
				isIndeterminate = item.allChildrenSelected === false;
			}

			const title = buildTitle(
				<Paragraph title={item.displayName} ellipsis={{ rows: 1 }}>
					{highlightedName}
				</Paragraph>,
				item,
				isItemExpanded
			);

			const customIconCheckbox = (
				<Checkbox
					id={`${dataIndex}-checkboxTree`}
					key={`${item.key}-${item.isChecked}`}
					indeterminate={isIndeterminate}
					checked={item.isChecked}
					disabled={roleId === 1}
					onChange={e => onChangeDeviceCheckbox(e, item)}
				/>
			);

			return {
				...item,
				title,
				icon: customIconCheckbox,
				children: mapDeviceRenderNode({
					treeData: item.children,
					searchType,
					expandedKeys,
					onChangeDeviceCheckbox,
				}),
			};
		});
	};

	const buildTitle = (highlightedName: React.ReactNode, item: ObjectLibraryTreeNode, isExpanded: boolean) => {
		let icon;
		const { imagePath, imageName } = imagePaths[item.extendedData.objectType];

		if (item.folderType === ObjectLibraryFolder.FOLDER) {
			icon = isExpanded ? <OpenedFolderIcon style={folderStyles} /> : <ClosedFolderIcon style={folderStyles} />;
		} else {
			icon = <img src={imagePath} className={styles.image} />;
		}

		return (
			<div className={styles.blockContainer}>
				<div className={styles.titleContainer}>
					{icon}
					{highlightedName}
				</div>
				<div className={styles.typeContainer}>
					<p className={styles.text}>{imageName}</p>
				</div>
				<div className={styles.addressContainer}>
					<p className={styles.text}>{item.extendedData.address}</p>
				</div>
			</div>
		);
	};

	const dataToRender = useMemo(() => {
		return mapDeviceRenderNode({
			treeData: rebuildTreeByFilters(searchType, data?.children, data),
			searchType,
			expandedKeys,
			onChangeDeviceCheckbox: handleOnChangeDeviceCheckbox,
		});
	}, [searchType, expandedKeys, data, rebuildTreeByFilters, mapDeviceRenderNode, handleOnChangeDeviceCheckbox]);

	return (
		<div className={styles.hardwareContainer}>
			<div className={styles.tableHeader}>
				<div className={styles.actionCheckbox}>{isAnyFilterApplied && <AppliedFilterIcon />}</div>
				<span className={styles.separator}>|</span>
				<div className={styles.deviceName}>
					<span>{_('DeviceName')}</span>
					<span className={styles.separator}>|</span>
				</div>
				<div className={styles.deviceType}>
					<span>{_('DeviceType')}</span>
					<span className={styles.separator}>|</span>
				</div>
				<div className={styles.Address}>
					<span>{_('Address')}</span>
				</div>
			</div>
			<div className={styles.containerDirectoryTree}>
				<Tree.DirectoryTree
					key={'Role-Hardware-HardwarePermission'}
					height={450}
					treeData={dataToRender}
					expandedKeys={expandedKeys}
					disabled={false}
					motion={false}
					selectedKeys={[]}
					autoExpandParent={autoExpandParent}
					expandAction={false}
					onExpand={handleOnExpand}
				/>
			</div>
		</div>
	);
};

export { HierarchyView };
